import React, {
  ReactChild, useState, useEffect, useCallback, useContext,
} from 'react'
import Loader from 'components/common/Loader'
import { toast } from 'react-toastify'
import {
  gql, useLazyQuery, useMutation, ApolloError, useApolloClient,
} from '@apollo/client'
import { Cognito } from 'components/auth/Cognito'
import { CognitoUser } from '@aws-amplify/auth'

// TODO: ideally this shouldn't be a constant, but not sure
// how to dynamically pull async in apollo when this Context
// depends on apollo. Maybe ApolloLink can be added below after
// client creation
// TODO: don't use localstorage, it's unsafe
// eslint-disable-next-line import/no-mutable-exports
export let Token: string | null = localStorage.getItem('token')

// TODO: pull in from graphql schema
export interface User {
  email?: string
  name?: string
  tenant?: {
    name?: string
    slug?: string
    flags?: {
      key: string
      value: string
    }[]
  }
}

export interface Session {
  user: User | null
  initialUrl: string
  setInitialUrl: (value: React.SetStateAction<string>) => void
  setToken: (value: React.SetStateAction<string | null>) => void
  login: (user: CognitoUser) => void
  logout: () => void
}

const initialContext: Session = {
  user: null,
  initialUrl: '/',
  setInitialUrl: (_value: React.SetStateAction<string>) => {},
  setToken: (_value: React.SetStateAction<string | null>) => {},
  login: () => {},
  logout: () => {},
}

const SessionContext = React.createContext(initialContext)

export const useSession = () => useContext(SessionContext)

export interface SessionProviderProps {
  children?: ReactChild
}

// locations fetch here is just to ensure user is admin
const CURRENT_USER = gql`
  {
    me {
      email
      name
      tenant {
        name
        slug
        flags {
          key
          value
        }
      }
    }
    locations {
      id
    }
  }
`

const AUTHORIZE = gql`
  mutation Authorize($jwt: String!) {
    authorize(jwt: $jwt) {
      token
    }
  }
`

const isUnauthorized = (err: ApolloError) => (
  (err?.graphQLErrors || []).some((gqlErr) => gqlErr.extensions?.code === 'FORBIDDEN')
)

export const SessionProvider: React.SFC<SessionProviderProps> = (props) => {
  const [token, setToken] = useState(Token)
  const [initialUrl, setInitialUrl] = useState<string>('/')
  const [loginMutation] = useMutation(AUTHORIZE)
  const [loadUser, {
    called, loading, error, data, networkStatus, refetch: refetchUser,
  }] = useLazyQuery(CURRENT_USER)
  const apollo = useApolloClient()

  Token = token
  if (token) {
    // TODO: don't use localstorage, it's unsafe
    localStorage.setItem('token', token)
  } else {
    // TODO: don't use localstorage, it's unsafe
    localStorage.removeItem('token')
  }

  const logout = useCallback(() => {
    setToken(null)
    apollo.clearStore()
    Cognito.signOut()
  }, [apollo])

  const login = useCallback(async (user: CognitoUser) => {
    const idToken = user.getSignInUserSession()?.getIdToken()

    try {
      const resp = await loginMutation({
        variables: {
          jwt: idToken?.getJwtToken(),
        },
      })
      Token = resp.data?.authorize?.token
      setToken(Token)
    } catch (loginErr) {
      if (isUnauthorized(loginErr)) {
        // react-router provider is nested under session provider,
        // so useHistory is not accessible here
        window.location.replace('/signup')
      }
    }
  }, [setToken, loginMutation])

  useEffect(() => {
    if (token && refetchUser) {
      refetchUser().catch((queryErr: ApolloError) => {
        if (isUnauthorized(queryErr)) {
          toast.error('user not authorized')
          logout()
        }
      })
    }
  }, [token, refetchUser, logout])

  const session: Session = {
    user: null,
    initialUrl,
    setInitialUrl,
    setToken,
    login,
    logout,
  }

  if (!token) {
    return (
      <SessionContext.Provider value={session}>
        {props.children}
      </SessionContext.Provider>
    )
  }

  session.user = data?.me || null

  if (!called) {
    loadUser()
    return <Loader />
  }

  if (error) {
    return <span>{error.message}</span>
  }

  if (loading || networkStatus < 7) {
    return <Loader title="Loading your account..." />
  }

  return (
    <SessionContext.Provider value={session}>
      {props.children}
    </SessionContext.Provider>
  )
}
